import { signInWithEmailAndPassword } from "firebase/auth";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../Components/Firebase/firebase";
import { onAuthStateChanged } from "firebase/auth";
import { toast } from "react-toastify";
import { 
    Box, 
    TextField, 
    Button, 
    Checkbox, 
    FormControlLabel, 
    Typography, 
    Paper,
    InputAdornment,
    IconButton,
    CircularProgress,
    Alert,
    Link,
    useTheme
} from '@mui/material';
import { Visibility, VisibilityOff, Mail, Lock } from '@mui/icons-material';
import axios from "axios";
import BASE_URL from "../config";


function Login() {
    const theme = useTheme();
    // State for input fields
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false); // Password visibility toggle
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate(); // React Router hook for navigation

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, (user) => {
            if (user) {
                navigate("/dashboard"); // redirect if user already logged in
            }
        });

        return () => unsubscribe(); // Clean up listener
    }, []);


    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            // 🔐 Firebase Authentication
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 🔑 Get Firebase Token
            const token = await user.getIdToken();

            // 📥 Fetch User Profile from API
            const response = await axios.get(`${BASE_URL}/api/users/me`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // ✅ Store organisation_id and role_id in state, context, or localStorage
            localStorage.setItem('organisation_id', response.data.organisation_id || '');
            localStorage.setItem('role_id', response.data.role_id || '');

            // ✅ Success Toast
            toast.success("User logged in Successfully", { position: "top-center" });

            // ➜ Navigate based on whether user has an organization
            if (response.data.organisation_id || response.data.isSuperAdmin) {
                navigate("/dashboard");
            } else {
                navigate("/onboarding");
            }

        } catch (error) {
            console.error("Login Error:", error);
            toast.error(error.message || "Login failed", { position: "bottom-center" });
        } finally {
            setLoading(false);
        }
    };


    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                padding: 2
            }}
        >
            <Paper
                elevation={0}
                sx={{
                    padding: { xs: 3, sm: 5 },
                    maxWidth: 450,
                    width: '100%',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'background.paper'
                }}
            >
                {/* Logo */}
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <img
                        src="/assets/images/EN_Logo.png"
                        alt="EncoDeNature Logo"
                        style={{ width: '160px', height: 'auto' }}
                    />
                </Box>

                {/* Welcome Message */}
                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography
                        variant="h4"
                        sx={{
                            fontWeight: 700,
                            color: 'text.primary',
                            mb: 1
                        }}
                    >
                        Welcome Back
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            color: 'text.secondary'
                        }}
                    >
                        Sign in to access your dashboard
                    </Typography>
                </Box>

                {/* Login Form */}
                <form onSubmit={handleSubmit}>
                    {/* Email Input */}
                    <TextField
                        fullWidth
                        label="Email Address"
                        type="email"
                        placeholder="Enter your email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        sx={{ mb: 2.5 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Mail sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Password Input */}
                    <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        sx={{ mb: 2 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Lock sx={{ color: 'text.secondary' }} />
                                </InputAdornment>
                            ),
                            endAdornment: password.length > 0 && (
                                <InputAdornment position="end">
                                    <IconButton
                                        onClick={() => setShowPassword(!showPassword)}
                                        edge="end"
                                        size="small"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    {/* Remember Me */}
                    <FormControlLabel
                        control={<Checkbox defaultChecked color="primary" />}
                        label="Remember me"
                        sx={{ mb: 3, '& .MuiTypography-root': { fontSize: '0.875rem' } }}
                    />

                    {/* Login Button */}
                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                            py: 1.5,
                            fontSize: '1rem',
                            fontWeight: 600,
                            textTransform: 'none',
                            borderRadius: 2
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={24} sx={{ color: 'white' }} />
                        ) : (
                            'Sign In'
                        )}
                    </Button>
                </form>

                {/* Forgot Password Notice */}
                <Alert 
                    severity="info" 
                    sx={{ 
                        mt: 3, 
                        borderRadius: 2,
                        '& .MuiAlert-message': { fontSize: '0.8125rem' }
                    }}
                >
                    <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
                        Forgot your password?
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        Password reset is not available at this time. Please contact your administrator or support team for assistance.
                    </Typography>
                </Alert>

                {/* Sign Up Link */}
                <Box sx={{ textAlign: 'center', mt: 3 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        Don't have an account?{' '}
                        <Link
                            href="/signup"
                            underline="hover"
                            sx={{ color: 'primary.main', fontWeight: 600 }}
                        >
                            Sign Up
                        </Link>
                    </Typography>
                </Box>
            </Paper>
        </Box>
    );
}

export default Login;
